import CountrySelector from 'components/CountrySelector'
import { Country } from 'components/CountrySelector/countries'
import FormInput from 'components/Forms/form-input'
import TimezoneSelector from 'components/TimezoneSelector'
import { Timezone } from 'components/TimezoneSelector/zones'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { WORD_SINGLE_LINE_REGEX, ZIP_REGEX } from 'types/common'
import { classNames } from 'utils/dom-helpers'
import { getTranslation } from 'utils/language-helper'
import ConfirmDialogModal from '../ConfirmDialog'
import { AddressFormValues } from './types'

function AddressForm({
    archiveClient,
    onFormSubmit
}: {
    archiveClient(id: string, first_name: string, last_name: string): void,
    onFormSubmit(): void
}) {
    const ctx = useAuth()
    const router = useRouter()
    const { translations } = useAppContext()
    const { attributes, setAttributes } = ctx.CreateClientModal
    const [prompt_archive_message, setRowPromptMessage] = useState('')
    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setError,
        setValue,
        reset,
    } = useForm<AddressFormValues>({
        mode: 'onSubmit',
    })

    useEffect(() => {
        if (attributes?.id) {
            setValue('street1', attributes?.street1 as string)
            setValue('city', attributes.city as string)
            setValue('state', attributes.state as string)
            setValue('country', attributes.country as string)
            setValue('zip', attributes.zip as string)
            setValue('timezone', attributes?.timezone as string)
        }
    }, [attributes?.id])

    async function handleClose() {
        reset()
        router.replace({
            pathname: router.pathname,
            query: null,
        })
        ctx.CreateClientModal.close()
    }

    const onSubmit: SubmitHandler<AddressFormValues> = async () => {
        onFormSubmit()
    }

    function handleArchiveCancel() {
        setRowPromptMessage('')
    }

    function handleOnChange(e: any) {
        const { name, value } = e.target
        setAttributes({
            ...attributes,
            [name]: value,
            isDirty: true,
        })
    }

    function handleCountryChange(v: Country) {
        setValue('country', v.name)
        setAttributes({
            ...attributes,
            country: v.name,
            isDirty: true,
        })
    }

    function handleTimezoneChange(v: Timezone) {
        setValue('timezone', v.text)
        setAttributes({
            ...attributes,
            timezone: v.text,
            isDirty: true,
        })
    }

    return (
        <form method="POST" onSubmit={handleSubmit(onSubmit)}>
            <div>
                <div className="pb-5 lg:flex">
                    <fieldset className="lg:w-1/2 lg:pr-8">
                        <FormInput
                            label='settings_address'
                            htmlFor="street1"
                            fieldError={errors.street1}
                        >
                            <input
                                type="text"
                                {...register('street1', {
                                    pattern: {
                                        value: WORD_SINGLE_LINE_REGEX,
                                        message: 'invalid.pattern.address'
                                    }
                                })}
                                onChange={handleOnChange}
                            />
                        </FormInput>
                    </fieldset>
                    <fieldset className="lg:w-1/2">
                        <FormInput
                            label='settings_city'
                            htmlFor="city"
                            fieldError={errors.city}
                        >
                            <input
                                type="text"
                                {...register('city', {
                                    pattern: {
                                        value: WORD_SINGLE_LINE_REGEX,
                                        message: 'invalid.pattern.city'
                                    }
                                })}
                                onChange={handleOnChange}
                            />
                        </FormInput>
                    </fieldset>
                </div>
                <div className="pb-5 lg:flex">
                    <fieldset className="lg:w-1/2 lg:pr-8">
                        <FormInput
                            label='settings_state'
                            htmlFor="state"
                            fieldError={errors.state}
                        >
                            <input
                                type="text"
                                {...register('state', {
                                    pattern: {
                                        value: WORD_SINGLE_LINE_REGEX,
                                        message: 'invalid.pattern.state'
                                    }
                                })}
                                onChange={handleOnChange}
                            />
                        </FormInput>
                    </fieldset>
                    <fieldset className="lg:w-1/2">
                        <FormInput
                            label='settings_country'
                            htmlFor="country"
                            fieldError={errors.country}
                            required
                        >
                            <Controller
                                control={control}
                                name="country"
                                rules={{ required: 'common.country_required' }}
                                render={({ field: { value } }) => (
                                    <CountrySelector
                                        id="country"
                                        onChange={handleCountryChange}
                                        error={errors.country?.type}
                                        defaultValue={value as string}
                                    />
                                )}
                            />
                        </FormInput>
                    </fieldset>
                </div>
                <div className=" lg:flex">
                    <fieldset className="lg:pr-8 lg:w-1/2">
                        <FormInput
                            label='settings_zipcode'
                            htmlFor="zip"
                            fieldError={errors.zip}
                        >
                            <input
                                type="text"
                                {...register('zip', {
                                    pattern: {
                                        value: ZIP_REGEX,
                                        message: 'invalid.pattern.zip'
                                    },
                                    maxLength: 10
                                })}
                                onChange={handleOnChange}
                            />
                        </FormInput>
                    </fieldset>
                    <fieldset className="w-1/2">
                        <FormInput
                            label='settings_timezone'
                            htmlFor="timezone"
                            fieldError={errors.timezone}
                            required
                        >
                            <Controller
                                control={control}
                                name="timezone"
                                rules={{ required: 'common.error_timezone_required' }}
                                render={({ field: { value } }) => (
                                    <TimezoneSelector
                                        id="timezone"
                                        onChange={handleTimezoneChange}
                                        error={errors.timezone?.type}
                                        defaultValue={value}
                                    />
                                )}
                            />
                        </FormInput>
                    </fieldset>
                </div>
                <div className='flex relative mt-4 justify-start'>
                    <button
                        type="button"
                        className='bg-red-100 text-red-500 btn-gap text-sm rounded-lg'
                        onClick={() => {
                            setRowPromptMessage(
                                getTranslation(
                                    'archive_client',
                                    translations,
                                    {
                                        first_name:
                                            attributes?.first_name as string,
                                        last_name:
                                            attributes?.last_name as string,
                                    }
                                )
                            )
                        }}
                    >
                        <i className="feather-archive" />
                    </button>
                    <div className='flex gap-3 absolute inset-y-0 right-0'>
                        <button
                            type="reset"
                            onClick={() => {
                                setAttributes({})
                                handleClose()
                            }}
                            className="border-gray-200 rounded-lg border btn-gap text-sm"
                        >
                            <Translation content_key="cancel" />
                        </button>
                        <button
                            type="submit"
                            className={classNames(
                                'group relative flex justify-center',
                                'btn-gap border border-transparent',
                                'rounded-md text-white text-sm',
                                'focus:outline-none',
                                !attributes?.isDirty
                                    ? 'bg-primary-light cursor-not-allowed'
                                    : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light cursor-pointer'
                            )}
                            disabled={!attributes?.isDirty}
                        >
                            <Translation content_key="save" />
                        </button>
                    </div>
                </div>
            </div>
            <ConfirmDialogModal
                prompt_message={prompt_archive_message}
                onConfirm={() => {
                    archiveClient(attributes?.id as string, attributes?.first_name as string, attributes?.last_name as string)
                    handleClose()
                }}
                onCancel={handleArchiveCancel}
            />
        </form>
    )
}

export default AddressForm
